/**
 * How a relationship group appears on an item's timeline and journey.
 *
 * A group is one of five types. Only a cluster is a cluster: a competing
 * (exclusive) group, a related group and a collection each carry their own
 * name and open their own page.
 */
import type { RelationshipGroupType } from '../types/prioritization/itemRelationships';

export type RelationshipGroupTimelineEventType =
  | 'package_formed'
  | 'cluster_formed'
  | 'relationship_added';

export interface RelationshipGroupTimelineEntry {
  eventType: RelationshipGroupTimelineEventType;
  /** Plain name of the group type, e.g. "Competing". */
  typeName: string;
  label: string;
  linkedLabel: string;
  linkedPath: string;
}

const GROUP_TYPE_NAMES: Record<RelationshipGroupType, string> = {
  package: 'Package',
  cluster: 'Cluster',
  exclusive: 'Competing',
  related_group: 'Related group',
  collection: 'Collection',
};

const GROUP_TYPE_PATHS: Record<RelationshipGroupType, string> = {
  package: '/prioritization/relationships/packages',
  cluster: '/prioritization/relationships/clusters',
  exclusive: '/prioritization/relationships/exclusives',
  related_group: '/prioritization/relationships',
  collection: '/prioritization/collections',
};

export function relationshipGroupTimelineEntry(group: {
  type: string;
  subtype?: string | null;
  title?: string | null;
}): RelationshipGroupTimelineEntry {
  const known = group.type in GROUP_TYPE_NAMES;
  const type = group.type as RelationshipGroupType;
  const typeName = known ? GROUP_TYPE_NAMES[type] : 'Relationship';
  const linkedPath = known ? GROUP_TYPE_PATHS[type] : '/prioritization/relationships';
  const linkedLabel = group.title ?? `View ${typeName.toLowerCase()}`;

  if (type === 'package') {
    return {
      eventType: 'package_formed',
      typeName,
      label: `Package · ${group.subtype ?? group.type}`,
      linkedLabel,
      linkedPath,
    };
  }
  if (type === 'cluster') {
    return { eventType: 'cluster_formed', typeName, label: group.title ?? typeName, linkedLabel, linkedPath };
  }
  return {
    eventType: 'relationship_added',
    typeName,
    label: group.title ? `${typeName} · ${group.title}` : typeName,
    linkedLabel,
    linkedPath,
  };
}
